import { AlertTriangle, Search, X } from "lucide-react";
import type { ChangeEvent, JSX } from "react";
import { useTranslation } from "react-i18next";

import { Select } from "@/shared/ui/select";

import { AUDIT_MODULES, AUDIT_RESULTS, DATE_PRESETS } from "../lib/url-state";
import type { AuditFilterState, DatePreset } from "../lib/url-state";

interface AuditFiltersProps {
  filter: AuditFilterState;
  onChange: (patch: Partial<AuditFilterState>) => void;
  onClear: () => void;
}

const CONTROL =
  "h-9 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring";
const PRESET_BTN =
  "inline-flex h-9 items-center gap-1.5 rounded-[var(--control-r)] border px-3 text-sm font-semibold transition focus-ring";

function hasActiveFilters(f: AuditFilterState): boolean {
  return Boolean(
    f.module || f.activity || f.actor || f.result || f.attention || f.search || f.preset !== "7d",
  );
}

/** `YYYY-MM-DDTHH:mm` (no timezone) <-> ISO-8601, for the native
 * `datetime-local` input backing the "custom" date preset. */
function toDatetimeLocalValue(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function AuditFilters({ filter, onChange, onClear }: AuditFiltersProps): JSX.Element {
  const { t } = useTranslation("audit");

  function handleModuleChange(e: ChangeEvent<HTMLSelectElement>): void {
    const v = e.target.value;
    onChange({ module: v === "all" ? undefined : (v as (typeof AUDIT_MODULES)[number]) });
  }

  function handleResultChange(e: ChangeEvent<HTMLSelectElement>): void {
    const v = e.target.value;
    onChange({ result: v === "all" ? undefined : (v as (typeof AUDIT_RESULTS)[number]) });
  }

  function handleAttentionToggle(): void {
    const next = filter.attention !== true;
    onChange({ attention: next ? true : undefined, result: undefined });
  }

  function handleActivityChange(e: ChangeEvent<HTMLInputElement>): void {
    onChange({ activity: e.target.value || undefined });
  }

  function handleActorChange(e: ChangeEvent<HTMLInputElement>): void {
    onChange({ actor: e.target.value || undefined });
  }

  function handleSearchChange(e: ChangeEvent<HTMLInputElement>): void {
    onChange({ search: e.target.value || undefined });
  }

  function handlePreset(preset: DatePreset): void {
    onChange({ preset, date_from: undefined, date_to: undefined });
  }

  function handleDateFromChange(e: ChangeEvent<HTMLInputElement>): void {
    onChange({ preset: "custom", date_from: fromDatetimeLocalValue(e.target.value) });
  }

  function handleDateToChange(e: ChangeEvent<HTMLInputElement>): void {
    onChange({ preset: "custom", date_to: fromDatetimeLocalValue(e.target.value) });
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="audit-filters">
      {/* Search */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]"
          aria-hidden
        />
        <input
          type="search"
          placeholder={t("filters.search_placeholder")}
          value={filter.search ?? ""}
          onChange={handleSearchChange}
          className={`${CONTROL} pl-8`}
          data-testid="audit-filter-search"
        />
      </div>

      {/* Module */}
      <Select
        value={filter.module ?? "all"}
        onChange={handleModuleChange}
        data-testid="audit-filter-module"
        aria-label={t("filters.all_modules")}
      >
        <option value="all">{t("filters.all_modules")}</option>
        {AUDIT_MODULES.map((m) => (
          <option key={m} value={m}>
            {t(`module.${m}`)}
          </option>
        ))}
      </Select>

      {/* Activity (free text — GlobalAuditEvent.activity has no closed enum) */}
      <input
        type="text"
        placeholder={t("filters.activity_placeholder")}
        value={filter.activity ?? ""}
        onChange={handleActivityChange}
        className={CONTROL}
        data-testid="audit-filter-activity"
        aria-label={t("filters.activity_placeholder")}
      />

      {/* Actor */}
      <input
        type="text"
        placeholder={t("filters.actor_placeholder")}
        value={filter.actor ?? ""}
        onChange={handleActorChange}
        className={CONTROL}
        data-testid="audit-filter-actor"
        aria-label={t("filters.actor_placeholder")}
      />

      {/* Needs attention — shorthand for result=failed,partial. Owns the
          Result control while on: the two would otherwise contradict each
          other (a single result can't also be "failed or partial"). */}
      <button
        type="button"
        onClick={handleAttentionToggle}
        aria-pressed={filter.attention === true}
        data-testid="audit-filter-attention"
        className={
          filter.attention === true
            ? `${PRESET_BTN} border-[var(--intent-danger-border)] bg-[var(--intent-danger-bg)] text-[var(--intent-danger-fg)]`
            : `${PRESET_BTN} border-[var(--premium-border-strong)] bg-white text-[var(--text-secondary)] hover:border-[var(--brand-500)]`
        }
      >
        <AlertTriangle className="h-4 w-4" aria-hidden />
        {t("filters.attention")}
      </button>

      {/* Result */}
      <Select
        value={filter.result ?? "all"}
        onChange={handleResultChange}
        disabled={filter.attention === true}
        className="disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="audit-filter-result"
        aria-label={t("filters.all_results")}
      >
        <option value="all">{t("filters.all_results")}</option>
        {AUDIT_RESULTS.map((r) => (
          <option key={r} value={r}>
            {t(`result.${r}`)}
          </option>
        ))}
      </Select>

      {/* Date presets */}
      {DATE_PRESETS.filter((p) => p !== "custom").map((preset) => (
        <button
          key={preset}
          type="button"
          onClick={() => handlePreset(preset)}
          aria-pressed={filter.preset === preset}
          data-testid={`audit-filter-preset-${preset}`}
          className={
            filter.preset === preset
              ? `${PRESET_BTN} border-[var(--brand-500)] bg-[var(--surface-spotlight)] text-[var(--brand-600)]`
              : `${PRESET_BTN} border-[var(--premium-border-strong)] bg-white text-[var(--text-secondary)] hover:border-[var(--brand-500)]`
          }
        >
          {t(`filters.date_${preset}`)}
        </button>
      ))}
      <button
        type="button"
        onClick={() => handlePreset("custom")}
        aria-pressed={filter.preset === "custom"}
        data-testid="audit-filter-preset-custom"
        className={
          filter.preset === "custom"
            ? `${PRESET_BTN} border-[var(--brand-500)] bg-[var(--surface-spotlight)] text-[var(--brand-600)]`
            : `${PRESET_BTN} border-[var(--premium-border-strong)] bg-white text-[var(--text-secondary)] hover:border-[var(--brand-500)]`
        }
      >
        {t("filters.date_custom")}
      </button>

      {filter.preset === "custom" && (
        <>
          <label className="flex items-center gap-1 text-sm text-[var(--text-secondary)]">
            {t("filters.date_from")}
            <input
              type="datetime-local"
              value={toDatetimeLocalValue(filter.date_from)}
              onChange={handleDateFromChange}
              data-testid="audit-filter-date-from"
              aria-label={t("filters.date_from")}
              className={CONTROL}
            />
          </label>
          <label className="flex items-center gap-1 text-sm text-[var(--text-secondary)]">
            {t("filters.date_to")}
            <input
              type="datetime-local"
              value={toDatetimeLocalValue(filter.date_to)}
              onChange={handleDateToChange}
              data-testid="audit-filter-date-to"
              aria-label={t("filters.date_to")}
              className={CONTROL}
            />
          </label>
        </>
      )}

      {hasActiveFilters(filter) && (
        <button
          type="button"
          onClick={onClear}
          data-testid="audit-filter-clear"
          className="inline-flex h-9 items-center gap-1.5 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
        >
          <X className="h-4 w-4" aria-hidden />
          {t("filters.clear")}
        </button>
      )}
    </div>
  );
}
